import { useState } from 'react';
import { C } from '../constants';

// ── LAB NAME ALIASES ──────────────────────────────────────────────────────
const ALIASES = {
  // Electrolytes
  'potassium':'K','pot':'K','k+':'K',
  'sodium':'Na','na+':'Na',
  'chloride':'Cl','cl-':'Cl',
  'bicarbonate':'HCO3','bicarb':'HCO3','hco3-':'HCO3','co2':'HCO3',
  'urea':'BUN','bun':'BUN','blood urea nitrogen':'BUN',
  'glucose':'RBS','fasting glucose':'FBS','fbs':'FBS','rbs':'RBS','bsl':'RBS',
  // Renal
  'creatinine':'Crea','creat':'Crea','cr':'Crea','crea':'Crea',
  'egfr':'eGFR',
  'calcium':'Ca','ca2+':'Ca',
  'magnesium':'Mg','mag':'Mg',
  'phosphate':'Phos','phosphorus':'Phos','phos':'Phos',
  // Blood
  'haemoglobin':'Hgb','hemoglobin':'Hgb','hgb':'Hgb','hb':'Hgb',
  'haematocrit':'Hct','hematocrit':'Hct','hct':'Hct','pcv':'Hct',
  'white blood cell':'WBC','white cells':'WBC','wcc':'WBC','leukocytes':'WBC',
  'platelets':'Plt','plt':'Plt','thrombocytes':'Plt',
  'neutrophils':'Neu','neutrophil count':'Neu','neut':'Neu','pmn':'Neu',
  'lymphocytes':'Lym','lymphocyte count':'Lym','lymph':'Lym',
  'monocytes':'Mon','eosinophils':'Eos',
  // Liver
  'ast':'AST','aspartate':'AST',
  'alt':'ALT','alanine':'ALT',
  'alp':'ALP','alkaline phosphatase':'ALP',
  'total bilirubin':'TB','bilirubin':'TB','bili':'TB',
  'direct bilirubin':'DB','indirect bilirubin':'IB',
  'albumin':'Alb',
  // Cardiac
  'troponin':'Trop','troponin i':'TropI','troponin t':'TropT',
  'ck':'CK','cpk':'CK','creatine kinase':'CK',
  'bnp':'BNP','pro-bnp':'proBNP','nt-probnp':'proBNP',
  // Coags
  'inr':'INR','pt':'PT','ptt':'PTT','aptt':'PTT','d-dimer':'D-Dimer','ddimer':'D-Dimer',
  'fibrinogen':'Fibrin',
  // Inflammatory
  'crp':'CRP','c-reactive protein':'CRP','c reactive protein':'CRP',
  'esr':'ESR','procalcitonin':'PCT','pct':'PCT',
  // Thyroid
  'tsh':'TSH','thyroid stimulating hormone':'TSH','ft4':'FT4','ft3':'FT3',
  't4':'FT4','free t4':'FT4',
  // Lipids
  'cholesterol':'TC','total cholesterol':'TC',
  'triglycerides':'Trig','tg':'Trig',
  'hdl':'HDL','ldl':'LDL',
  // DM
  'hba1c':'HbA1c','a1c':'HbA1c','glycated haemoglobin':'HbA1c',
  // ABG
  'ph':'pH','paco2':'PaCO2','pao2':'PaO2','base excess':'Base Excess',
  'lactate':'Lactate','lactic acid':'Lactate',
  // Urine
  'urine protein':'Protein','urine glucose':'Glucose',
};

const normaliseName = (raw) => {
  const key = raw.toLowerCase().trim().replace(/[^a-z0-9\s\-+]/g, '').replace(/\s+/g, ' ');
  return ALIASES[key] || ALIASES[key.replace(/\s/g, '')] || raw.trim();
};

const normaliseFlag = (raw) => {
  if (!raw) return '';
  const s = raw.toUpperCase().trim();
  if (['H','HIGH','HH','CRIT H','^'].some(x => s.startsWith(x))) return 'H';
  if (['L','LOW','LL','CRIT L','V'].some(x => s.startsWith(x))) return 'L';
  return '';
};

// ── JS LAB PARSER ─────────────────────────────────────────────────────────
// Handles most formats without requiring AI
export const parseLabText = (text) => {
  const results = [];
  const seen = new Set();

  const addResult = (name, value, unit, flag) => {
    const n = normaliseName(name);
    const f = normaliseFlag(flag);
    if (!n || !value) return;
    const key = n.toLowerCase() + value;
    if (seen.has(key)) return;
    seen.add(key);
    results.push({ name: n, value: value.trim(), unit: (unit || '').trim(), flag: f });
  };

  // Remove reference ranges like (3.5-5.0) or [3.5-5.0] or ref 3.5-5.0
  const clean = text.replace(/\([\d.]+\s*[-–]\s*[\d.]+\s*[a-z/%²³]*\)/gi, '')
                    .replace(/\[[\d.]+\s*[-–]\s*[\d.]+\s*[a-z/%²³]*\]/gi, '')
                    .replace(/\bref(?:erence)?\s*:?\s*[\d.]+\s*[-–]\s*[\d.]+[^\n]*/gi, '')
                    .replace(/normal:?\s*[\d.]+\s*[-–]\s*[\d.]+[^\n]*/gi, '');

  const lines = clean.split(/[\n;]+/);

  lines.forEach(line => {
    const t = line.trim();
    if (!t) return;

    // Format: "NAME: VALUE UNIT FLAG" or "NAME VALUE UNIT FLAG"
    // Matches: "K: 4.2", "CREATININE: 142 H", "Hgb 98 g/L L", "Na 138 mmol/L"
    const m1 = t.match(/^([A-Za-z][A-Za-z0-9\s\-+/()]*?)\s*:?\s+([\d.]+)\s*([a-zA-Z/%²³µ·*]{0,12})?\s*([HLhl]{0,2})\s*$/);
    if (m1) {
      addResult(m1[1], m1[2], m1[3] || '', m1[4] || '');
      return;
    }

    // Format: "NAME VALUE UNIT (H)" — flag in parens
    const m2 = t.match(/^([A-Za-z][A-Za-z0-9\s\-+/]*?)\s+([\d.]+)\s*([a-zA-Z/%²³µ·*]{0,12})?\s*\(([HLhl]+)\)/);
    if (m2) {
      addResult(m2[1], m2[2], m2[3] || '', m2[4]);
      return;
    }

    // Format: Tabular with multiple spaces/tabs — "Na              138       mmol/L"
    const m3 = t.match(/^([A-Za-z][A-Za-z0-9\s\-+]*?)\s{2,}([\d.]+)\s*([a-zA-Z/%²³µ·*]{0,12})?/);
    if (m3) {
      const flagMatch = t.match(/\s([HLhl]{1,2})\s*$/);
      addResult(m3[1], m3[2], m3[3] || '', flagMatch ? flagMatch[1] : '');
      return;
    }
  });

  // Also scan for inline mentions: "K came back 4.2", "Na 131", "Cr up to 180"
  const inlinePattern = /\b(K\+?|Na|Cl|Ca|Mg|Cr|Hb|Hgb|WBC|Plt|INR|CRP|TSH|Trop(?:onin)?|HbA1c|BNP|eGFR|pH|pCO2|pO2|ALT|AST|ALP|BUN|HCO3)\b\s+(?:came back\s+|up to\s+|is\s+|was\s+|of\s+)?([\d.]+)\s*([a-zA-Z/%]{0,8})?/gi;
  let im;
  while ((im = inlinePattern.exec(text)) !== null) {
    addResult(im[1], im[2], im[3] || '', '');
  }

  return results;
};

export default function ParseLabsModal({ onClose, onParsed }) {
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [editing, setEditing] = useState([]);

  const doParse = () => {
    const results = parseLabText(text);
    setParsed(results);
    setEditing(results.map(r => ({ ...r, id: 'pe' + Date.now() + Math.random() })));
  };

  const updateRow = (id, field, val) => setEditing(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));
  const removeRow = (id) => setEditing(prev => prev.filter(r => r.id !== id));

  const confirm = () => {
    const valid = editing.filter(r => r.name && r.value);
    onParsed(valid);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,4,0.85)', zIndex: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
      <div style={{ width: '100%', maxWidth: 600, background: C.bg1, borderRadius: '16px 16px 0 0', border: '1px solid ' + C.bd, borderBottom: 'none', maxHeight: '85vh', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid ' + C.bd, flexShrink: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#E0EEFA', flex: 1 }}>📋 Parse Labs</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 20, cursor: 'pointer', padding: '0 4px' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
          {!parsed ? (
            <>
              <div style={{ fontSize: 12, color: C.dim, marginBottom: 10, lineHeight: 1.5 }}>
                Paste lab results in any format — from a portal, PDF, message, or typed manually.
              </div>
              <div style={{ fontSize: 10, color: C.dim2, marginBottom: 8 }}>Examples accepted:</div>
              <div style={{ fontSize: 11, color: C.dim2, fontFamily: "'IBM Plex Mono', monospace", background: C.bg3, borderRadius: 8, padding: '8px 12px', marginBottom: 12, lineHeight: 1.8 }}>
                K 4.2 mmol/L<br />
                CREATININE: 142 H<br />
                Hgb 98 g/L (L)<br />
                Na 138, K 3.9, Cr 110<br />
                his K came back 6.1
              </div>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Paste lab results here..."
                autoFocus
                style={{ width: '100%', minHeight: 160, background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 10, padding: '12px', color: C.tx, fontSize: 13, fontFamily: "'IBM Plex Mono', monospace", outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 }}
              />
              <button onClick={doParse} disabled={!text.trim()}
                style={{ width: '100%', marginTop: 10, padding: '13px 0', borderRadius: 10, background: text.trim() ? '#1A3D6A' : C.bg2, color: text.trim() ? '#7DC4FF' : C.dim2, border: '1px solid ' + (text.trim() ? '#4A9EFF44' : C.bd), fontSize: 14, fontWeight: 800, cursor: text.trim() ? 'pointer' : 'default' }}>
                Parse →
              </button>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: editing.length > 0 ? '#30D158' : '#FF9500', flex: 1 }}>
                  {editing.length > 0 ? `Found ${editing.length} result${editing.length > 1 ? 's' : ''}` : 'No results found — try editing manually below'}
                </span>
                <button onClick={() => { setParsed(null); setEditing([]); }} style={{ background: 'none', border: 'none', color: C.dim, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>← Back</button>
              </div>

              {/* Editable results table */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.6fr auto', gap: 5, marginBottom: 6 }}>
                {['Test','Value','Unit','Flag',''].map((h, i) => (
                  <div key={i} style={{ fontSize: 8, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', paddingLeft: 2 }}>{h}</div>
                ))}
              </div>
              {editing.map(r => (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.6fr auto', gap: 5, marginBottom: 6, alignItems: 'center' }}>
                  <input value={r.name} onChange={e => updateRow(r.id, 'name', e.target.value)}
                    style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 12, fontWeight: 600 }} />
                  <input value={r.value} onChange={e => updateRow(r.id, 'value', e.target.value)}
                    style={{ background: C.bg3, border: '1.5px solid ' + C.bd, borderRadius: 6, padding: '6px 8px', color: '#E0EEFA', fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }} />
                  <input value={r.unit} onChange={e => updateRow(r.id, 'unit', e.target.value)} placeholder="unit"
                    style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 6, padding: '6px 6px', color: C.dim, fontSize: 11 }} />
                  <select value={r.flag} onChange={e => updateRow(r.id, 'flag', e.target.value)}
                    style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 6, color: r.flag === 'H' ? '#FF4545' : r.flag === 'L' ? '#64D2FF' : C.tx, fontSize: 12, padding: '5px 2px', fontWeight: 700 }}>
                    <option value="">—</option><option value="H">H</option><option value="L">L</option>
                  </select>
                  <button onClick={() => removeRow(r.id)} style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}>×</button>
                </div>
              ))}
              {editing.length === 0 && (
                <div style={{ textAlign: 'center', color: C.dim2, padding: '20px 0', fontSize: 13 }}>No results parsed. Try a different format.</div>
              )}
            </>
          )}
        </div>

        {parsed && editing.length > 0 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid ' + C.bd, flexShrink: 0 }}>
            <button onClick={confirm}
              style={{ width: '100%', padding: '13px 0', borderRadius: 10, background: '#081A0C', color: '#30D158', border: '1px solid #1A3A1E', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
              ✓ Add {editing.filter(r => r.name && r.value).length} Results to Labs
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
